import { useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useEvaluateArchivo } from "@/features/evaluacion/hooks/useRetroApi";
import { useCreateIntento } from "@/hooks/intentos/useIntentos";
import { EvaluacionService } from "../services/evaluacion.service";
import type {
  EvaluacionContext,
  EvaluacionCallbacks,
  EvaluacionResult,
} from "../types/evaluacion.types";

export interface UseEvaluacionOptions {
  context: EvaluacionContext;
  callbacks?: EvaluacionCallbacks;
}

export interface UseEvaluacionReturn {
  // Estado
  feedback: string;
  isProcessing: boolean;
  error: string | null;

  // Acciones
  evaluarArchivo: (file: File) => Promise<EvaluacionResult | null>;
  canEvaluate: (file: File | null, intentosUsados: number) => boolean;
  resetFeedback: () => void;
}

/**
 * Hook principal para manejar el proceso completo de evaluación
 * Orquesta la subida de archivo, evaluación y creación de intento
 */
export function useEvaluacion({
  context,
  callbacks,
}: UseEvaluacionOptions): UseEvaluacionReturn {
  const queryClient = useQueryClient();
  const evaluateArchivo = useEvaluateArchivo();
  const { mutateAsync: createIntento } = useCreateIntento();

  const [feedback, setFeedback] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  const resetFeedback = useCallback(() => {
    setFeedback("");
    setError(null);
  }, []);

  const canEvaluate = useCallback(
    (file: File | null, intentosUsados: number): boolean => {
      const validation = EvaluacionService.canEvaluate(
        file,
        intentosUsados,
        context.limiteIntentos
      );

      if (!validation.valid) {
        setError(validation.reason || "No se puede evaluar");
        return false;
      }

      return true;
    },
    [context.limiteIntentos]
  );

  const evaluarArchivo = useCallback(
    async (file: File): Promise<EvaluacionResult | null> => {
      try {
        setError(null);

        // 1. Subir archivo
        setFeedback("Subiendo archivo...");
        callbacks?.onUploadStart?.();

        const uploadResult = await EvaluacionService.uploadFile({
          file,
          actividadId: context.actividadId,
        });

        if (!uploadResult?.llave && !uploadResult?.url) {
          throw new Error("No se pudo obtener la llave o URL del archivo subido");
        }

        callbacks?.onUploadSuccess?.(uploadResult);

        // 2. Evaluar archivo
        setFeedback("Archivo subido. Generando retroalimentación...");
        callbacks?.onEvaluateStart?.();

        const evalResult = await evaluateArchivo.mutateAsync({
          file: uploadResult.llave ?? uploadResult.url,
          file_clean: context.archivoLimpioKey ?? context.archivoLimpioUrl,
          index: context.cerebro,
          programa: context.programa,
          actividad: context.actividadId,
        });

        if (!evalResult || !evalResult.base_feedback) {
          throw new Error("No se recibió retroalimentación del servidor");
        }

        callbacks?.onEvaluateSuccess?.(evalResult);

        // 3. Crear intento
        setFeedback("Retroalimentación generada. Registrando intento...");

        const intentoDto = EvaluacionService.prepareIntentoDto({
          actividadAprendizId: context.actividadAprendizId,
          archivo: uploadResult,
          retro: evalResult.base_feedback,
          sessionId: evalResult.session_id,
          sessionPayload: JSON.stringify(evalResult),
        });

        const intentoResult = await createIntento(intentoDto);
        callbacks?.onIntentoCreated?.(intentoResult.id);

        // 4. Invalidar queries para refrescar datos
        await queryClient.invalidateQueries({
          queryKey: [
            "actividadAprendiz",
            context.actividadId,
            context.aprendizId,
          ],
        });

        setFeedback("✅ Evaluación completada exitosamente");

        const result: EvaluacionResult = {
          uploadResult,
          evaluationResult: evalResult,
          intentoId: intentoResult.id,
        };

        callbacks?.onComplete?.(result);

        return result;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Error desconocido en la evaluación";

        setError(errorMessage);
        setFeedback(`❌ Error: ${errorMessage}`);

        const stage = feedback.includes("Subiendo")
          ? "upload"
          : feedback.includes("Generando")
          ? "evaluate"
          : "intento";

        callbacks?.onError?.(
          err instanceof Error ? err : new Error(errorMessage),
          stage
        );

        return null;
      }
    },
    [context, callbacks, evaluateArchivo, createIntento, queryClient, feedback]
  );

  return {
    feedback,
    isProcessing: evaluateArchivo.isPending,
    error,
    evaluarArchivo,
    canEvaluate,
    resetFeedback,
  };
}
